import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { PayPalWrapper as PayPalCheckout } from '../components/PayPalWrapper';
import NewsletterOptIn from '../components/NewsletterOptIn';
import { useAuth } from '../context/AuthContext';
import SEOHead from '../components/SEOHead';

const API_URL = "";

// Registration/checkout pages are noindexed — they're transactional, keyed by
// a workshop id in the query string, and have no evergreen content value.
// Self-referencing canonical still applies in case a crawler ignores noindex.
const RegisterSEO = () => (
  <SEOHead title="Workshop Registration" canonicalPath="/register" noindex />
);

export const Register = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const workshopId = searchParams.get('workshop');
  const { user, token, isAuthenticated } = useAuth();

  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);
  // Server-verified pricing — source of truth for what the buyer pays
  const [verifiedPricing, setVerifiedPricing] = useState(null); // { price, price_tier, eligible, reason }
  const [pricingLoading, setPricingLoading] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    specialRequirements: '',
    newsletterOptIn: false
  });
  const [showPayPal, setShowPayPal] = useState(false);
  const [joiningWaitlist, setJoiningWaitlist] = useState(false);
  const [onWaitlist, setOnWaitlist] = useState(false);

  // Fetch workshops from API
  useEffect(() => {
    const fetchWorkshops = async () => {
      try {
        const res = await fetch(`${API_URL}/api/content/workshops`);
        if (res.ok) {
          const data = await res.json();
          setWorkshops(data);
        }
      } catch (err) {
        console.error('Error fetching workshops:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchWorkshops();
  }, []);

  // Auto-fill from logged-in user
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        firstName: prev.firstName || user.first_name || '',
        lastName:  prev.lastName  || user.last_name  || '',
        email:     prev.email     || user.email       || '',
      }));
    }
  }, [user]);

  // Get current workshop using useMemo
  const workshop = useMemo(() => {
    return workshops.find(w => w.id === parseInt(workshopId));
  }, [workshops, workshopId]);

  // Fetch server-verified pricing whenever workshop or auth state changes
  useEffect(() => {
    if (!workshopId) return;
    const fetchPrice = async () => {
      setPricingLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/payments/workshop-price/${workshopId}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (res.ok) {
          const data = await res.json();
          setVerifiedPricing(data);
        }
      } catch (err) {
        console.error('Error fetching verified price:', err);
      } finally {
        setPricingLoading(false);
      }
    };
    fetchPrice();
  }, [workshopId, token]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setShowPayPal(false);
  };

  // Price is always from server — never from client selection
  const getPrice = () => verifiedPricing?.price ?? (workshop?.nonMemberPrice || 0);
  const isMemberPrice = verifiedPricing?.price_tier === 'member';

  const getWorkshopItems = () => {
    if (!workshop) return [];
    return [{
      name: workshop.title,
      description: `Workshop with ${workshop.instructor} - ${workshop.schedule}`,
      quantity: 1,
      price: getPrice(),
      category: 'workshop'
    }];
  };

  const handleJoinWaitlist = async () => {
    if (!formData.firstName || !formData.lastName || !formData.email) {
      toast.error('Please fill in your name and email');
      return;
    }

    setJoiningWaitlist(true);
    try {
      const response = await fetch(`${API_URL}/api/waitlist/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workshop_id: workshopId,
          workshop_name: workshop.title,
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          phone: formData.phone
        })
      });

      if (response.ok) {
        const data = await response.json();
        setOnWaitlist(true);
        toast.success(`You're #${data.position} on the waitlist! We'll email you when a spot opens.`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to join waitlist');
      }
    } catch (error) {
      toast.error('Error joining waitlist');
    } finally {
      setJoiningWaitlist(false);
    }
  };

  const handleProceedToPayment = (e) => {
    e.preventDefault();

    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    setShowPayPal(true);
  };

  const handlePaymentSuccess = (result) => {
    const registrationData = {
      ...formData,
      workshopId: workshop.id,
      workshopTitle: workshop.title,
      instructor: workshop.instructor,
      dates: workshop.schedule,
      price: getPrice(),
      price_tier: verifiedPricing?.price_tier,
      paymentId: result.payment_id,
      status: 'paid',
      timestamp: new Date().toISOString()
    };

    localStorage.setItem(`workshop_registration_${Date.now()}`, JSON.stringify(registrationData));

    toast.success('Registration complete!', {
      description: `You're registered for ${workshop.title}. Check your email for confirmation.`
    });

    setTimeout(() => {
      navigate('/workshops');
    }, 2000);
  };

  const handlePaymentError = (error) => {
    toast.error('Payment failed. Please try again.');
    console.error('Payment error:', error);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white py-16">
        <RegisterSEO />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-stone-600">Loading workshop details...</p>
        </div>
      </div>
    );
  }

  if (!workshop) {
    return (
      <div className="min-h-screen bg-white py-16">
        <RegisterSEO />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl font-serif text-stone-900 mb-4">Workshop Not Found</h1>
          <Button onClick={() => navigate('/workshops')} className="bg-cafa-red hover:bg-cafa-red-900 text-white">
            Back to Workshops
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="workshop-register-page">
      <RegisterSEO />
      {/* Header */}
      <section className="bg-gradient-to-b from-stone-50 to-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button
            onClick={() => navigate('/workshops')}
            variant="outline"
            className="mb-6 border-stone-300 text-stone-700 hover:bg-stone-50"
          >
            ← Back to Workshops
          </Button>
          <h1 className="text-4xl font-serif text-stone-900 mb-4">Workshop Registration</h1>
          <p className="text-xl text-stone-600">Complete your registration for this workshop</p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Workshop Details */}
            <div className="lg:col-span-1">
              <Card className="border-stone-200 sticky top-24">
                <CardHeader>
                  <CardTitle className="text-xl font-serif">Workshop Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-stone-900 mb-1">{workshop.title}</h3>
                    <p className="text-sm text-cafa-red">{workshop.instructor}</p>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-semibold text-stone-700">Dates:</span>
                      <p className="text-stone-600">{workshop.schedule}</p>
                    </div>
                    <div>
                      <span className="font-semibold text-stone-700">Time:</span>
                      <p className="text-stone-600">{workshop.time}</p>
                    </div>
                    <div>
                      <span className="font-semibold text-stone-700">Location:</span>
                      <p className="text-stone-600">{workshop.location}</p>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-stone-200">
                    <p className="text-sm font-semibold text-stone-700 mb-2">Pricing:</p>
                    <div className="space-y-1 text-sm">
                      <p className="text-stone-600">Members: <span className="font-bold text-cafa-red">${workshop.memberPrice}</span></p>
                      <p className="text-stone-600">Non-Members: <span className="font-bold text-stone-700">${workshop.nonMemberPrice}</span></p>
                    </div>
                  </div>

                  {workshop.materials && (
                    <div className="pt-4 border-t border-stone-200">
                      <p className="text-sm font-semibold text-stone-700 mb-1">Materials:</p>
                      <p className="text-xs text-stone-600">{workshop.materials}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Registration Form */}
            <div className="lg:col-span-2">
              <Card className="border-stone-200">
                <CardHeader>
                  <CardTitle className="text-xl font-serif">Registration Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleProceedToPayment} className="space-y-6">
                    {/* Name Fields */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="firstName" className="text-stone-700">First Name *</Label>
                        <Input
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          required
                          className="mt-2"
                          data-testid="workshop-firstname"
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName" className="text-stone-700">Last Name *</Label>
                        <Input
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          required
                          className="mt-2"
                          data-testid="workshop-lastname"
                        />
                      </div>
                    </div>

                    {/* Contact Info */}
                    <div>
                      <Label htmlFor="email" className="text-stone-700">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="mt-2"
                        data-testid="workshop-email"
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone" className="text-stone-700">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="mt-2"
                      />
                    </div>

                    {/* Address */}
                    <div>
                      <Label htmlFor="address" className="text-stone-700">Street Address</Label>
                      <Input
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        className="mt-2"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="city" className="text-stone-700">City</Label>
                        <Input id="city" name="city" value={formData.city} onChange={handleChange} className="mt-2" />
                      </div>
                      <div>
                        <Label htmlFor="state" className="text-stone-700">State</Label>
                        <Input id="state" name="state" value={formData.state} onChange={handleChange} className="mt-2" />
                      </div>
                      <div>
                        <Label htmlFor="zip" className="text-stone-700">ZIP Code</Label>
                        <Input id="zip" name="zip" value={formData.zip} onChange={handleChange} className="mt-2" />
                      </div>
                    </div>

                    {/* ── Server-Verified Pricing Banner ─────────────────────────── */}
                    {pricingLoading ? (
                      <div className="rounded-lg border border-stone-200 bg-stone-50 p-4 text-sm text-stone-500 animate-pulse">
                        Verifying pricing eligibility…
                      </div>
                    ) : verifiedPricing?.eligible ? (
                      /* Active / Lifetime member — member price granted */
                      <div className="rounded-lg border border-green-300 bg-green-50 p-4" data-testid="member-price-banner">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="inline-block w-2 h-2 rounded-full bg-green-500"></span>
                          <span className="text-sm font-semibold text-green-800">Member Price Applied</span>
                        </div>
                        <p className="text-xs text-green-700">
                          Your active Coppini membership has been verified. You'll be charged the member rate.
                        </p>
                      </div>
                    ) : verifiedPricing?.reason === 'inactive_member' ? (
                      /* Logged in but inactive member */
                      <div className="rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="inactive-member-banner">
                        <p className="text-sm font-semibold text-amber-800 mb-1">
                          Your membership is not currently active.
                        </p>
                        <p className="text-xs text-amber-700 mb-2">
                          Please renew to receive member pricing. Non-member price will be charged.
                        </p>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="border-amber-400 text-amber-800 hover:bg-amber-100 text-xs"
                          onClick={() => navigate('/membership')}
                          data-testid="renew-membership-btn"
                        >
                          Renew Membership
                        </Button>
                      </div>
                    ) : (
                      /* Not logged in */
                      <div className="rounded-lg border border-stone-300 bg-stone-50 p-4" data-testid="non-member-price-banner">
                        <p className="text-sm font-semibold text-stone-700 mb-1">
                          Member pricing requires login with an active Coppini membership.
                        </p>
                        <p className="text-xs text-stone-500 mb-2">
                          Non-member price will be charged. Log in to check your member eligibility.
                        </p>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="border-stone-400 text-stone-700 hover:bg-stone-100 text-xs"
                          onClick={() => navigate(`/members/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`)}
                          data-testid="login-for-member-price-btn"
                        >
                          Log in for member pricing
                        </Button>
                      </div>
                    )}
                    {/* ──────────────────────────────────────────────────────────── */}

                    {/* Special Requirements */}
                    <div>
                      <Label htmlFor="specialRequirements" className="text-stone-700">
                        Special Requirements or Questions (Optional)
                      </Label>
                      <Textarea
                        id="specialRequirements"
                        name="specialRequirements"
                        value={formData.specialRequirements}
                        onChange={handleChange}
                        rows={4}
                        className="mt-2"
                        placeholder="Dietary restrictions, accessibility needs, questions about materials, etc."
                      />
                    </div>

                    {/* Newsletter Opt-In */}
                    <NewsletterOptIn
                      checked={formData.newsletterOptIn}
                      onChange={(checked) => setFormData({ ...formData, newsletterOptIn: checked })}
                      source="workshop_registration"
                    />

                    {/* Total — server-verified price */}
                    <div className={`rounded-lg p-4 border ${isMemberPrice ? 'bg-green-50 border-green-200' : 'bg-stone-50 border-stone-200'}`}>
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="text-lg font-semibold text-stone-900">Total Amount:</span>
                          {isMemberPrice && (
                            <span className="ml-2 text-xs font-semibold px-2 py-0.5 bg-green-100 text-green-800 rounded" data-testid="member-price-label">
                              Member Price
                            </span>
                          )}
                        </div>
                        <span className={`text-3xl font-bold ${isMemberPrice ? 'text-green-700' : 'text-cafa-red'}`} data-testid="workshop-price-display">
                          ${getPrice()}
                        </span>
                      </div>
                    </div>

                    {/* PayPal or Submit Button */}
                    {showPayPal ? (
                      <div className="space-y-4">
                        <PayPalCheckout
                          items={getWorkshopItems()}
                          payerEmail={formData.email}
                          payerName={`${formData.firstName} ${formData.lastName}`}
                          onSuccess={handlePaymentSuccess}
                          onError={handlePaymentError}
                          onCancel={() => setShowPayPal(false)}
                          authToken={token}
                          registrationData={{
                            type: 'workshop',
                            workshop_id: workshopId,
                            workshop_name: workshop?.title,
                            attendee_info: formData,
                            price_paid: getPrice(),
                            price_tier: verifiedPricing?.price_tier,
                            newsletter_opt_in: formData.newsletterOptIn
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowPayPal(false)}
                          className="w-full"
                        >
                          Back to Edit
                        </Button>
                      </div>
                    ) : (
                      <Button
                        type="submit"
                        className="w-full bg-cafa-red hover:bg-cafa-red-900 text-white text-lg py-6"
                        data-testid="workshop-submit"
                      >
                        Proceed to Payment
                      </Button>
                    )}

                    <p className="text-xs text-stone-500 text-center">
                      By registering, you agree to our cancellation policy. Full refund available up to 30 days before the workshop.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

